import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Alert, CircularProgress, Stack, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
  ToggleButton, ToggleButtonGroup, Collapse,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import axios from 'axios';
import Filters from './Filters';
import PdfPreviewDialog from './components/PdfPreviewDialog';
import SearchSelect from './components/SearchSelect';
import { apiUrl, downloadBlob, handleApiError } from './utils/api';
import { formatDate } from './utils/date';
import { useRole } from '../auth/RoleContext';

function BreakdownRow({ b }) {
  const [open, setOpen] = useState(false);
  const tickets = b.tickets || [];
  return (
    <>
      <TableRow>
        <TableCell sx={{ width: 40 }}>
          {tickets.length > 0 && (
            <IconButton size="small" onClick={() => setOpen((v) => !v)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{b.code}</TableCell>
        <TableCell align="right">{b.ticketCount ?? ''}</TableCell>
        <TableCell align="right">{b.tons != null ? Number(b.tons).toFixed(2) : ''}</TableCell>
        <TableCell align="right">{Number(b.revenue || 0).toFixed(2)}</TableCell>
      </TableRow>
      {tickets.length > 0 && (
        <TableRow>
          <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ mx: 2, my: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>FS Code</TableCell>
                      <TableCell align="right">Tons</TableCell>
                      <TableCell align="right">Total after company ($)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.map((t, i) => (
                      <TableRow key={t.ticketNo || i}>
                        <TableCell>{t.ticketNo ?? ''}</TableCell>
                        <TableCell>{formatDate(t.ticketDate) || ''}</TableCell>
                        <TableCell>{t.fsCode || ''}</TableCell>
                        <TableCell align="right">{t.tons != null ? Number(t.tons).toFixed(2) : ''}</TableCell>
                        <TableCell align="right">{Number(t.revenue || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function PayrollRow({ row, payBasis, codeLabel, adj, onAdj, onSave, onStatement, onPreview }) {
  const [open, setOpen] = useState(false);
  const breakdown = (payBasis === 'JOB_CODE' ? row.jobCodeBreakdown : row.fsCodeBreakdown) || [];
  const fieldBreakdown = row.fieldBreakdown || [];
  const hasDetail = breakdown.length > 0 || fieldBreakdown.length > 0;
  const deductions = Number(adj?.deductions) || 0;
  const bonus = Number(adj?.bonus) || 0;
  const withholding = Number(adj?.withholding) || 0;
  // Net = Gross − Deductions + Bonus − Withholding (all three are per-payslip manual entries).
  const net = (Number(row.gross) || 0) - deductions + bonus - withholding;
  return (
    <>
      <TableRow>
        <TableCell>
          {hasDetail && (
            <IconButton size="small" onClick={() => setOpen((v) => !v)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{row.driverName || row.driver}</TableCell>
        <TableCell>{row.truck || row.truckNumber || ''}</TableCell>
        <TableCell align="right">{Number(row.weekRevenue || 0).toFixed(2)}</TableCell>
        <TableCell align="right">{Number(row.sharePct || 0).toFixed(2)}</TableCell>
        <TableCell align="right">{Number(row.gross || 0).toFixed(2)}</TableCell>
        <TableCell align="right">
          <TextField type="number" size="small" value={adj?.deductions ?? ''} placeholder="0"
            onChange={(e) => onAdj('deductions', e.target.value)} onBlur={onSave}
            inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }} sx={{ width: 90 }} />
        </TableCell>
        <TableCell align="right">
          <TextField type="number" size="small" value={adj?.bonus ?? ''} placeholder="0"
            onChange={(e) => onAdj('bonus', e.target.value)} onBlur={onSave}
            inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }} sx={{ width: 90 }} />
        </TableCell>
        <TableCell align="right">
          <TextField type="number" size="small" value={adj?.withholding ?? ''} placeholder="0"
            onChange={(e) => onAdj('withholding', e.target.value)} onBlur={onSave}
            inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }} sx={{ width: 90 }} />
        </TableCell>
        <TableCell align="right">{net.toFixed(2)}</TableCell>
        <TableCell align="center">
          <IconButton
            size="small"
            title="Preview payslip"
            onClick={() => onPreview(row.driverId, row.driverName)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            title="Download payslip"
            onClick={() => onStatement(row.driverId, row.driverName)}
          >
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      {hasDetail && (
        <TableRow>
          <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ m: 2 }}>
                {breakdown.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Plant work — breakdown by {codeLabel}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 40 }} />
                          <TableCell>{codeLabel}</TableCell>
                          <TableCell align="right">Tickets</TableCell>
                          <TableCell align="right">Tons</TableCell>
                          <TableCell align="right">Revenue ($)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {breakdown.map((b, i) => (
                          <BreakdownRow key={b.code || i} b={b} />
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
                {fieldBreakdown.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: breakdown.length > 0 ? 2 : 0 }}>
                      Field work — per load / hour
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 40 }} />
                          <TableCell>Type / Basis</TableCell>
                          <TableCell align="right">Tickets</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Revenue ($)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fieldBreakdown.map((b, i) => (
                          <BreakdownRow key={b.code || i} b={b} />
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function Payroll() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [payBasis, setPayBasis] = useState('FS_CODE'); // FS_CODE | JOB_CODE
  // Plant filter: ticket company names ([] = all plants). Options = plants with tickets in the searched range.
  const [plantOptions, setPlantOptions] = useState([]);
  const [plants, setPlants] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewName, setPreviewName] = useState('payslip.pdf');
  // Per-row manual adjustments (bonus / withholding), keyed by driver+truck. Session-only for now.
  const [adjust, setAdjust] = useState({});
  const rowKey = (r) => `${r.driverId}-${r.truck || r.truckNumber || ''}`;
  const setAdj = (key, field, val) => setAdjust((a) => ({ ...a, [key]: { ...(a[key] || {}), [field]: val } }));
  // Load saved adjustments for the week (Monday) and key them the same way the rows are keyed.
  const loadAdjustments = async (weekStart) => {
    if (!weekStart) { setAdjust({}); return; }
    try {
      const res = await axios.get(`${apiUrl}/api/payroll/adjustments`, { params: { weekStart } });
      const map = {};
      (res.data || []).forEach((a) => {
        map[`${a.driverId}-${a.truck || ''}`] = {
          bonus: a.bonus ?? '', withholding: a.withholding ?? '', deductions: a.deductions ?? '',
        };
      });
      setAdjust(map);
    } catch { setAdjust({}); }
  };
  // Persist one row's adjustment (upsert) — called on blur of a bonus/withholding/deductions field.
  const saveAdjustment = async (r) => {
    if (!filters?.startDate) return;
    const a = adjust[rowKey(r)] || {};
    const numOrNull = (v) => (v === '' || v == null ? null : Number(v));
    try {
      await axios.put(`${apiUrl}/api/payroll/adjustments`, {
        driverId: r.driverId,
        truck: r.truck || r.truckNumber || '',
        weekStart: filters.startDate,
        bonus: numOrNull(a.bonus),
        withholding: numOrNull(a.withholding),
        deductions: numOrNull(a.deductions),
      });
    } catch (err) {
      setError(handleApiError(err, 'Failed to save adjustment'));
    }
  };
  // Sum a driver's bonus/withholding across all their rows (a driver may have several truck segments).
  const driverAdj = (driverId) => rows.filter((r) => r.driverId === driverId).reduce((acc, r) => {
    const a = adjust[rowKey(r)] || {};
    return {
      bonus: acc.bonus + (Number(a.bonus) || 0),
      withholding: acc.withholding + (Number(a.withholding) || 0),
      deductions: acc.deductions + (Number(a.deductions) || 0),
    };
  }, { bonus: 0, withholding: 0, deductions: 0 });
  const { role } = useRole();
  const isIvy = role === 'IVY_STAFF';
  // IVY's calls the FS code the "Job Code" and has no separate job-code basis.
  const codeLabel = isIvy ? 'Job Code' : (payBasis === 'JOB_CODE' ? 'Job Code' : 'FS Code');

  // Repeat the key for list params (plants=a&plants=b) so Spring binds them to a List.
  const listParams = { indexes: null };

  const loadPlantOptions = async (filterData) => {
    try {
      const res = await axios.get(`${apiUrl}/api/payroll/plants`, {
        params: { startDate: filterData.startDate, endDate: filterData.endDate },
      });
      setPlantOptions(Array.isArray(res.data) ? res.data : []);
    } catch { setPlantOptions([]); }
  };

  // Populate the Plant options for the current week on open (the Filters default), so the dropdown is
  // usable before the first Search; each Search then reloads them for the searched range.
  useEffect(() => {
    const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const monday = new Date();
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    loadPlantOptions({ startDate: ymd(monday), endDate: ymd(sunday) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runQuery = async (filterData, basis, plantSel = plants) => {
    setLoading(true);
    setError('');
    setInfo('');
    setRows([]);
    try {
      const params = {
        startDate: filterData.startDate,
        endDate: filterData.endDate,
        driverId: filterData.driver,
        payBasis: basis,
        plants: plantSel,
      };
      const response = await axios.get(`${apiUrl}/api/payroll`, { params, paramsSerializer: listParams });
      const data = Array.isArray(response.data) ? response.data : (response.data?.rows || []);
      setRows(data);
      await loadAdjustments(filterData.startDate);
      if (data.length === 0) setInfo('No matching records found.');
    } catch (err) {
      setError(handleApiError(err, 'Failed to load payroll'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (filterData) => {
    setFilters(filterData);
    loadPlantOptions(filterData);
    await runQuery(filterData, payBasis);
  };

  const handlePlantChange = async (v) => {
    setPlants(v);
    if (filters) await runQuery(filters, payBasis, v);
  };

  const handleBasisChange = async (_, v) => {
    if (!v) return;
    setPayBasis(v);
    if (filters) await runQuery(filters, v);
  };

  const payslipName = (driverId, driverName) =>
    `payslip-${(driverName || driverId).toString().replace(/[^\w-]+/g, '_')}-${filters?.startDate || ''}.pdf`;

  const downloadStatement = async (driverId, driverName) => {
    if (!filters) return;
    try {
      const { bonus, withholding, deductions } = driverAdj(driverId);
      const response = await axios.get(`${apiUrl}/api/payroll/${driverId}/statement/pdf`, {
        params: { startDate: filters.startDate, endDate: filters.endDate, payBasis, bonus, withholding, deductions, simple: !isIvy, plants },
        paramsSerializer: listParams,
        responseType: 'blob',
      });
      downloadBlob(response, payslipName(driverId, driverName));
    } catch (err) {
      setError(handleApiError(err, 'Failed to download payslip'));
    }
  };

  // Preview: fetch the same payslip PDF as a blob and show it in the review dialog before printing.
  const previewStatement = async (driverId, driverName) => {
    if (!filters) return;
    try {
      const { bonus, withholding, deductions } = driverAdj(driverId);
      const response = await axios.get(`${apiUrl}/api/payroll/${driverId}/statement/pdf`, {
        params: { startDate: filters.startDate, endDate: filters.endDate, payBasis, bonus, withholding, deductions, simple: !isIvy, plants },
        paramsSerializer: listParams,
        responseType: 'blob',
      });
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewName(payslipName(driverId, driverName));
    } catch (err) {
      setError(handleApiError(err, 'Failed to preview payslip'));
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
  };

  const totals = rows.reduce(
    (acc, r) => {
      const a = adjust[rowKey(r)] || {};
      const deductions = Number(a.deductions) || 0;
      const bonus = Number(a.bonus) || 0;
      const withholding = Number(a.withholding) || 0;
      const net = (Number(r.gross) || 0) - deductions + bonus - withholding;
      return {
        revenue: acc.revenue + (Number(r.weekRevenue) || 0),
        gross: acc.gross + (Number(r.gross) || 0),
        deductions: acc.deductions + deductions,
        bonus: acc.bonus + bonus,
        withholding: acc.withholding + withholding,
        net: acc.net + net,
      };
    },
    { revenue: 0, gross: 0, deductions: 0, bonus: 0, withholding: 0, net: 0 }
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Weekly Driver Payroll</Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography>Pay Basis:</Typography>
        <ToggleButtonGroup
          color="primary"
          exclusive
          value={payBasis}
          onChange={handleBasisChange}
          size="small"
        >
          <ToggleButton value="FS_CODE">{isIvy ? 'Job Code' : 'FS Code'}</ToggleButton>
          {!isIvy && <ToggleButton value="JOB_CODE">Job Code</ToggleButton>}
        </ToggleButtonGroup>
        <Box sx={{ minWidth: 280, maxWidth: 520 }}>
          <SearchSelect multiple label="Plant"
            placeholder={plantOptions.length ? 'All plants' : 'No plant tickets this week'}
            disabled={plantOptions.length === 0 && plants.length === 0}
            options={Array.from(new Set([...plantOptions, ...plants]))}
            getOptionLabel={(c) => c}
            getOptionValue={(c) => c}
            value={plants}
            onChange={handlePlantChange} />
        </Box>
      </Stack>
      {plants.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2 }}>
          Paying only tickets from the selected plant(s). Field tickets aren't tied to a plant, so they're left out.
        </Typography>
      )}

      <Filters
        ScreenName="Payroll"
        onFilterChange={handleSearch}
        showDriver
        hideCompany
        snapWeek
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
      {loading && <CircularProgress />}

      {rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Driver</TableCell>
                <TableCell>Truck</TableCell>
                <TableCell align="right">Week Revenue ($)</TableCell>
                <TableCell align="right">Share %</TableCell>
                <TableCell align="right">Gross ($)</TableCell>
                <TableCell align="right">Deductions ($)</TableCell>
                <TableCell align="right">Bonus ($)</TableCell>
                <TableCell align="right">Withholding ($)</TableCell>
                <TableCell align="right">Net ($)</TableCell>
                <TableCell align="center">Payslip</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <PayrollRow
                  key={r.driverId ? `${r.driverId}-${r.truck}` : i}
                  row={r}
                  payBasis={payBasis}
                  codeLabel={codeLabel}
                  adj={adjust[rowKey(r)]}
                  onAdj={(field, val) => setAdj(rowKey(r), field, val)}
                  onSave={() => saveAdjustment(r)}
                  onStatement={downloadStatement}
                  onPreview={previewStatement}
                />
              ))}
              <TableRow>
                <TableCell colSpan={3} align="right"><strong>Totals</strong></TableCell>
                <TableCell align="right"><strong>{totals.revenue.toFixed(2)}</strong></TableCell>
                <TableCell />
                <TableCell align="right"><strong>{totals.gross.toFixed(2)}</strong></TableCell>
                <TableCell align="right"><strong>{totals.deductions.toFixed(2)}</strong></TableCell>
                <TableCell align="right"><strong>{totals.bonus.toFixed(2)}</strong></TableCell>
                <TableCell align="right"><strong>{totals.withholding.toFixed(2)}</strong></TableCell>
                <TableCell align="right"><strong>{totals.net.toFixed(2)}</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PdfPreviewDialog
        open={!!previewUrl}
        onClose={closePreview}
        url={previewUrl}
        title="Payslip — Preview"
        fileName={previewName}
      />
    </Box>
  );
}
